import { useEnvironmentInterface } from "environment-interface";
import { useBranchEditor, useEditorFeature } from "features/editor/Feature";
import {
  fetchCsbBranches,
  fetchGitHubBranches,
} from "hooks/api/useRepositoryBranches";
import { useSession } from "hooks/api/useSession";
import { DisposableStore } from "vscode/monaco";

import { useActiveFile } from "hooks/useActiveFile";
import { useBranchingAnimation } from "hooks/useBranchingAnimation";
import { useEditorRouter } from "hooks/useEditorRouter";

import type { Command } from "../../types";

const LOADING_BRANCHES_LABEL = "$(sync~spin) Loading Branches...";

export const useToggleBranchPickerCommand = (): Command => {
  const { monacoEditor } = useEnvironmentInterface();
  const router = useEditorRouter();
  const { owner, repo, workspace, defaultBranchName } = useBranchEditor();
  const [_, editorDispatch] = useEditorFeature();
  const [__, activeFileActions] = useActiveFile();
  const { startBranchingAnimation } = useBranchingAnimation();
  const [session, sessionApi] = useSession();

  const command: Command = {
    id: "codesandbox.git.goToBranch",
    name: "Go to Branch...",
    section: "Git",
    binding: "$mod+Shift+B",
    perform: async () => {
      const disposableStore = new DisposableStore();
      const vscode = await monacoEditor.getExtensionApi();

      type QuickPickItem =
        | {
            kind: 0;
            label: string;
            description?: string;
            loading?: boolean;
            isCreateBranch?: boolean;
          }
        | {
            kind: -1;
            label: string;
          };

      const quickpicker = disposableStore.add(
        vscode.window.createQuickPick<QuickPickItem>(),
      );

      quickpicker.title = "Go To Branch";
      quickpicker.placeholder = "Select a branch";
      quickpicker.busy = true;

      quickpicker.items = [
        { kind: 0, label: defaultBranchName, description: "Default" },
        { kind: 0, label: LOADING_BRANCHES_LABEL, loading: true },
      ];

      /**
       * Will add the create branch item to the top of the list if the user has typed a branch name and no
       * branch is selected.
       */
      function triggerCreateBranchItem() {
        if (quickpicker.value === "") {
          quickpicker.items = quickpicker.items.filter(
            (item) => item.kind === -1 || !item.isCreateBranch,
          );

          return;
        }

        if (quickpicker.activeItems.length !== 0) {
          const firstItem = quickpicker.activeItems[0];
          if (firstItem.kind === -1 || !firstItem.isCreateBranch) {
            const isExactMatch = quickpicker.value === firstItem.label;
            if (isExactMatch) {
              return;
            }
          }
        }

        quickpicker.items = [
          {
            label: "Create New Branch Called '" + quickpicker.value + "'...",
            kind: 0 as const,
            isCreateBranch: true,
          },
          ...quickpicker.items.filter(
            (item) => item.kind === -1 || !item.isCreateBranch,
          ),
        ];
      }

      let csb: NonNullable<
        Awaited<ReturnType<typeof fetchCsbBranches>>["project"]
      >["branches"] = [];
      let gh: Awaited<ReturnType<typeof fetchGitHubBranches>>["branches"] = [];

      const updateBranches = () => {
        if (disposableStore.isDisposed) {
          return;
        }

        type Writable<T> = { -readonly [P in keyof T]: T[P] };
        const items: Writable<(typeof quickpicker)["items"]> = [
          { kind: 0, label: defaultBranchName, description: "Default" },
        ];

        if (csb.length) {
          csb.sort((a, b) => {
            const B = new Date(b?.lastAccessedAt || "").getTime();
            const A = new Date(a?.lastAccessedAt || "").getTime();
            return B - A;
          });

          items.push({ kind: -1, label: "CodeSandbox" });
          items.push(
            ...csb
              .filter((branch) => branch.name !== defaultBranchName)
              .map((branch) => ({
                label: branch.name,
                kind: 0 as const,
              })),
          );
        }

        const csbBranchNames = new Set(csb.map((branch) => branch.name));

        if (gh.length) {
          const filteredGhBranches = gh.filter(
            (branch) =>
              branch.name !== defaultBranchName &&
              !csbBranchNames.has(branch.name),
          );

          if (filteredGhBranches.length) {
            items.push({ kind: -1, label: "GitHub" });
            items.push(
              ...filteredGhBranches.map((branch) => ({
                label: branch.name,
                kind: 0 as const,
              })),
            );
          }
        }

        quickpicker.items = items;
        quickpicker.busy = false;

        setTimeout(() => {
          triggerCreateBranchItem();
        });
      };

      fetchCsbBranches(owner, repo, workspace?.id ?? null).then((result) => {
        if (!result.project) {
          return;
        }

        csb = result.project.branches;

        updateBranches();
      });

      fetchGitHubBranches(owner, repo).then((result) => {
        gh = result.branches;

        updateBranches();
      });

      disposableStore.add(
        quickpicker.onDidChangeValue(() => {
          triggerCreateBranchItem();
        }),
      );

      function switchBranch(create: boolean, branchName: string) {
        if (session.state !== "AUTHENTICATED") {
          sessionApi.signIn({
            type: "github",
          });

          return;
        }

        startBranchingAnimation();

        activeFileActions.unset();

        // We only seamlessly transition to a new branch if you are creating one from the current.
        // If you are navigating to a different branch we need a full refresh or VSCode can have
        // problematic state on the models
        if (create) {
          editorDispatch({
            type: "SWITCH_BRANCH",
            name: branchName,
            create,
          });
        } else {
          window.location.href =
            window.location.origin +
            router.instance.url("github", {
              owner,
              repo,
              branch: branchName,
            }) +
            (workspace ? `?workspaceId=${workspace.id}` : "");
        }
      }

      disposableStore.add(
        quickpicker.onDidAccept(() => {
          const chosenItem = quickpicker.activeItems[0];
          if (!chosenItem) {
            return;
          }

          if (chosenItem.kind === -1) {
            return;
          }

          if (chosenItem.isCreateBranch) {
            switchBranch(true, quickpicker.value);
            disposableStore.dispose();
          } else {
            if (chosenItem.label === LOADING_BRANCHES_LABEL) {
              return;
            }

            switchBranch(false, chosenItem.label);
            disposableStore.dispose();
          }
        }),
      );

      quickpicker.show();
    },
  };

  return command;
};
